import React, { useState, useEffect } from 'react';
import { Image, Trash2, Plus, X, ExternalLink, Check, Search, Upload } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';

interface MediaItem {
  id: string;
  url: string;
  name: string;
  type: string;
  size: number;
  createdAt: string;
}

interface MediaLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
  title?: string;
}

const MediaLibrary: React.FC<MediaLibraryProps> = ({ 
  isOpen, 
  onClose, 
  onSelect,
  title = "Bibliothèque de Médias"
}) => {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadMedia();
    }
  }, [isOpen]);

  const loadMedia = async () => {
    try {
      setLoading(true);
      
      // This is a mock implementation - in a real app, you would fetch from your storage
      // For now, we'll use some placeholder images
      const mockMedia: MediaItem[] = [
        {
          id: '1',
          url: 'https://images.pexels.com/photos/3819830/pexels-photo-3819830.jpeg',
          name: 'electric-motorcycle-1.jpg',
          type: 'image/jpeg',
          size: 1024000,
          createdAt: new Date().toISOString()
        },
        {
          id: '2',
          url: 'https://images.pexels.com/photos/2393835/pexels-photo-2393835.jpeg',
          name: 'electric-motorcycle-2.jpg',
          type: 'image/jpeg',
          size: 1548000,
          createdAt: new Date().toISOString()
        },
        {
          id: '3',
          url: 'https://images.pexels.com/photos/13035553/pexels-photo-13035553.jpeg',
          name: 'electric-motorcycle-3.jpg',
          type: 'image/jpeg',
          size: 2048000,
          createdAt: new Date().toISOString()
        },
        {
          id: '4',
          url: 'https://images.pexels.com/photos/2611675/pexels-photo-2611675.jpeg',
          name: 'electric-motorcycle-4.jpg',
          type: 'image/jpeg',
          size: 1324000,
          createdAt: new Date().toISOString()
        },
        {
          id: '5',
          url: 'https://images.pexels.com/photos/1413412/pexels-photo-1413412.jpeg',
          name: 'electric-motorcycle-5.jpg',
          type: 'image/jpeg',
          size: 1824000,
          createdAt: new Date().toISOString()
        },
        {
          id: '6',
          url: 'https://images.pexels.com/photos/13691727/pexels-photo-13691727.jpeg',
          name: 'electric-motorcycle-6.jpg',
          type: 'image/jpeg',
          size: 1624000,
          createdAt: new Date().toISOString()
        },
        {
          id: '7',
          url: 'https://images.pexels.com/photos/8439094/pexels-photo-8439094.jpeg',
          name: 'baas-architecture.jpg',
          type: 'image/jpeg',
          size: 1924000,
          createdAt: new Date().toISOString()
        },
        {
          id: '8',
          url: 'https://images.pexels.com/photos/7988079/pexels-photo-7988079.jpeg',
          name: 'management-platform.jpg',
          type: 'image/jpeg',
          size: 1724000,
          createdAt: new Date().toISOString()
        },
        {
          id: '9',
          url: 'https://images.pexels.com/photos/6633920/pexels-photo-6633920.jpeg',
          name: 'user-app.jpg',
          type: 'image/jpeg',
          size: 1524000,
          createdAt: new Date().toISOString()
        },
        {
          id: '10',
          url: 'https://images.pexels.com/photos/6633926/pexels-photo-6633926.jpeg',
          name: 'merchant-app.jpg',
          type: 'image/jpeg',
          size: 1424000,
          createdAt: new Date().toISOString()
        },
        {
          id: '11',
          url: 'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg',
          name: 'team-meeting.jpg',
          type: 'image/jpeg',
          size: 1324000,
          createdAt: new Date().toISOString()
        },
        {
          id: '12',
          url: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg',
          name: 'team-collaboration.jpg',
          type: 'image/jpeg',
          size: 1224000,
          createdAt: new Date().toISOString()
        }
      ];
      
      setMediaItems(mockMedia);
    } catch (error) {
      console.error('Error loading media:', error);
      toast.error('Erreur lors du chargement des médias');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExternalImage = () => {
    if (!newImageUrl.trim()) {
      toast.error('Veuillez entrer une URL valide');
      return;
    }
    
    // Simple URL validation
    try {
      new URL(newImageUrl);
    } catch (e) {
      toast.error('URL invalide');
      return;
    }
    
    const newItem: MediaItem = {
      id: Date.now().toString(),
      url: newImageUrl,
      name: newImageUrl.split('/').pop() || 'image.jpg',
      type: 'image/jpeg', // Assuming JPEG for external URLs
      size: 0, // Size unknown for external URLs
      createdAt: new Date().toISOString()
    };
    
    setMediaItems([newItem, ...mediaItems]);
    setNewImageUrl('');
    toast.success('Image ajoutée avec succès');
  };

  const handleDeleteMedia = (id: string) => {
    setMediaItems(mediaItems.filter(item => item.id !== id));
    toast.success('Média supprimé avec succès');
  };

  const handleSelectMedia = (url: string) => {
    setSelectedItem(url);
    onSelect(url);
  };

  const handleConfirmSelection = () => {
    if (selectedItem) {
      onSelect(selectedItem);
      onClose();
    } else {
      toast.error('Veuillez sélectionner une image');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    try {
      setUploadingFile(true);
      
      // In a real implementation, you would upload to Supabase Storage
      // For now, we'll just simulate the upload with a timeout
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Create a new media item from the file
      const file = files[0];
      const reader = new FileReader();
      
      reader.onload = (event) => {
        if (event.target && event.target.result) {
          const newItem: MediaItem = {
            id: Date.now().toString(),
            url: URL.createObjectURL(file),
            name: file.name,
            type: file.type,
            size: file.size,
            createdAt: new Date().toISOString()
          };
          
          setMediaItems([newItem, ...mediaItems]);
          toast.success('Image téléchargée avec succès');
        }
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Erreur lors du téléchargement du fichier');
    } finally {
      setUploadingFile(false);
    }
  };

  const filteredMedia = mediaItems.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <button 
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-500"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Rechercher des médias..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="url"
                  placeholder="Ajouter une image externe (URL)"
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  className="px-3 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <button
                onClick={handleAddExternalImage}
                className="px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                <Plus size={18} />
              </button>
              <label className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors cursor-pointer">
                <input 
                  type="file" 
                  className="hidden" 
                  accept="image/*"
                  onChange={handleFileUpload}
                  disabled={uploadingFile}
                />
                {uploadingFile ? (
                  <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <Upload size={18} />
                )}
              </label>
            </div>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-600"></div>
            </div>
          ) : filteredMedia.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <Image size={48} className="mb-4" />
              <p className="text-lg font-medium">Aucun média trouvé</p>
              <p className="text-sm">Ajoutez des images en utilisant l'URL ci-dessus</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {filteredMedia.map((item) => (
                <div 
                  key={item.id} 
                  className={`relative group border rounded-lg overflow-hidden cursor-pointer ${
                    selectedItem === item.url ? 'ring-2 ring-primary-500 border-primary-500' : 'border-gray-200 hover:border-primary-300'
                  }`}
                  onClick={() => handleSelectMedia(item.url)}
                >
                  <div className="aspect-w-1 aspect-h-1">
                    <img 
                      src={item.url} 
                      alt={item.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150?text=Error';
                      }}
                    />
                  </div>
                  
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="flex gap-2">
                      <a 
                        href={item.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-1.5 bg-white/80 rounded-full text-gray-700 hover:bg-white transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink size={16} />
                      </a>
                      <button 
                        className="p-1.5 bg-red-500/80 rounded-full text-white hover:bg-red-500 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteMedia(item.id);
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  {selectedItem === item.url && (
                    <div className="absolute top-2 right-2 bg-primary-500 text-white p-1 rounded-full">
                      <Check size={14} />
                    </div>
                  )}
                  
                  <div className="p-2 bg-white text-xs truncate">
                    {item.name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="p-4 border-t border-gray-200 flex justify-between items-center bg-gray-50">
          <div className="text-sm text-gray-500">
            {filteredMedia.length} éléments
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleConfirmSelection}
              disabled={!selectedItem}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Sélectionner
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaLibrary;